import { formatCurrencyNumberFromString } from 'util/numbers.util';
import type { Order, Row } from '../table.organism';

function numericComparator(a: number, b: number) {
  if (b === a) {
    return 0;
  }

  if (b < a || typeof b === 'undefined') {
    return -1;
  }

  if (b > a || typeof a === 'undefined') {
    return 1;
  }

  return 0;
}

function stringComparator(a: string, b: string) {
  if (a === b) {
    return 0;
  }

  if (b?.toLowerCase() < a?.toLowerCase() || typeof b === 'undefined') {
    return -1;
  }

  if (b?.toLowerCase() > a?.toLowerCase() || typeof a === 'undefined') {
    return 1;
  }

  return 0;
}

// Strict formats so plain text (e.g. "JAMES", "A$AP") is not misread as a time/currency value
const TIME_FORMAT = /^\d{1,2}:\d{2}\s*(AM|PM)$/i;
const CURRENCY_FORMAT = /^-?\$[\d,]+(\.\d+)?$/;

// "h:mm AM/PM" -> minutes since midnight; NaN if not a time
function timeToMinutes(value: string) {
  const match = value.trim().match(TIME_FORMAT);
  if (!match) {
    return NaN;
  }

  const [hours, minutes] = value.trim().split(':');
  const hour = Number(hours) % 12;
  const isPm = match[1].toUpperCase() === 'PM';

  return (isPm ? hour + 12 : hour) * 60 + Number(minutes.slice(0, 2));
}

export function timeComparator(a: string, b: string) {
  return numericComparator(timeToMinutes(a), timeToMinutes(b));
}

export function descendingComparator<T>(a: Row<T>, b: Row<T>, orderBy: keyof T) {
  if (typeof a.data[orderBy] !== typeof b.data[orderBy]) {
    const first = a.data[orderBy] as unknown as string | number;
    const second = b.data[orderBy] as unknown as string | number;

    return stringComparator(first?.toString(), second?.toString());
  } else if (typeof a.data[orderBy] === 'string') {
    const first = a.data[orderBy] as unknown as string;
    const second = b.data[orderBy] as unknown as string;

    // Require BOTH values to match a special format; otherwise fall back to string
    // comparison. This keeps the comparator symmetric and stops substrings like the
    // "AM" in "JAMES" or a stray "$" in a name from being treated as time/currency.
    if (CURRENCY_FORMAT.test(first) && CURRENCY_FORMAT.test(second)) {
      return numericComparator(formatCurrencyNumberFromString(first), formatCurrencyNumberFromString(second));
    }

    if (TIME_FORMAT.test(first) && TIME_FORMAT.test(second)) {
      return timeComparator(first, second);
    }

    return stringComparator(first, second);
  } else {
    const first = a.data[orderBy] as unknown as number;
    const second = b.data[orderBy] as unknown as number;

    return numericComparator(first, second);
  }
}

export function getComparator<T>(order: Order, orderBy: keyof T): (a: Row<T>, b: Row<T>) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
